import type {
  ChipContent,
  EmbedContent,
  ImageContent,
  LatexContent,
  McqContent,
  RevealStep,
  ScriptElement,
  ShapeContent,
  Slide,
  ThreeDContent,
  VideoContent,
} from './schema.types';

export interface NarrationElementContext {
  type: ScriptElement['type'];
  summary: string;
}

/** Flatten every element on a slide into a quick `{id -> element}` lookup. */
export function indexSlideElements(slide: Slide): Map<string, ScriptElement> {
  const map = new Map<string, ScriptElement>();
  for (const row of slide.rows) {
    for (const col of row.columns) {
      for (const el of col.elements) {
        map.set(el.id, el);
      }
    }
  }
  return map;
}

/** Build a short, language-model-friendly description of an element. */
export function summarizeElement(el: ScriptElement): NarrationElementContext {
  return { type: el.type, summary: summarizeContent(el) };
}

function summarizeContent(el: ScriptElement): string {
  const c = el.content;
  switch (el.type) {
    case 'text':
      return clean(typeof c === 'string' ? stripHtml(c) : '') || '(empty text)';
    case 'image': {
      const img = (c as ImageContent | undefined) ?? { src: '' };
      return img.alt ? `image: ${img.alt}` : `image (${shortenUrl(img.src)})`;
    }
    case 'video': {
      const v = (c as VideoContent | undefined) ?? { src: '' };
      return `video clip (${shortenUrl(v.src)})`;
    }
    case 'mcq': {
      const m = c as McqContent | undefined;
      if (!m) return 'multiple-choice question';
      const opts = (m.options ?? []).map((o, i) => `(${i + 1}) ${o}`).join('  ');
      const correct =
        typeof m.answerIndex === 'number' && m.options?.[m.answerIndex]
          ? ` — correct: ${m.options[m.answerIndex]}`
          : '';
      return `MCQ — Q: ${m.question}  Options: ${opts}${correct}`;
    }
    case 'chip': {
      const ch = (c as ChipContent | undefined) ?? { label: '' };
      return `chip: ${ch.label}`;
    }
    case 'shape': {
      const s = (c as ShapeContent | undefined) ?? { shape: 'rect' };
      return `${s.shape} shape${s.fill ? ` (${s.fill})` : ''}`;
    }
    case 'embed': {
      const em = (c as EmbedContent | undefined) ?? { url: '' };
      return `embedded simulation (${shortenUrl(em.url)})`;
    }
    case '3d': {
      const t = (c as ThreeDContent | undefined) ?? { src: '' };
      return t.alt ? `3D model: ${t.alt}` : `3D model (${shortenUrl(t.src)})`;
    }
    case 'latex': {
      const l = (c as LatexContent | undefined) ?? { source: '' };
      return `equation: ${l.source}`;
    }
    default:
      return el.type;
  }
}

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>(?!\s*<)/gi, ' ')
    .replace(/<\/?[a-z][^>]*>/gi, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function clean(text: string): string {
  return text.length > 280 ? `${text.slice(0, 277)}…` : text;
}

function shortenUrl(url: string | undefined): string {
  if (!url) return '';
  if (url.startsWith('data:')) return 'inline data';
  return url.length > 80 ? `${url.slice(0, 77)}…` : url;
}

export interface NarrationStepContext {
  slideLabel: string;
  stepLabel: string;
  elements: NarrationElementContext[];
  previousNarrations: string[];
}

/** Compact "what is on this slide" summary used for slide-level AI prompts. */
export function summarizeSlide(slide: Slide): {
  label: string;
  summary: string;
  narrations: string[];
} {
  const idx = indexSlideElements(slide);
  const elementSummaries = Array.from(idx.values()).map(
    (el) => `[${el.type}] ${summarizeContent(el)}`,
  );
  const summary = elementSummaries.join(' · ').slice(0, 1500);
  const narrations: string[] = [];
  for (const step of slide.revealSteps ?? []) {
    const t = step.audio?.text?.trim();
    if (t) narrations.push(t);
  }
  return {
    label: slide.label || 'Untitled slide',
    summary,
    narrations,
  };
}

/** Build all the contextual information Claude needs for one reveal step. */
export function buildStepContext(
  slide: Slide,
  step: RevealStep,
): NarrationStepContext {
  const idx = indexSlideElements(slide);
  const elements: NarrationElementContext[] = [];
  for (const id of step.elementIds ?? []) {
    const el = idx.get(id);
    if (el) elements.push(summarizeElement(el));
  }
  // Annotations are visual cues — include their target context too.
  for (const a of step.annotations ?? []) {
    const el = idx.get(a.targetElementId);
    if (el) {
      elements.push({
        type: el.type,
        summary: `${a.kind} on ${summarizeElement(el).summary}${
          a.text ? ` — note: ${a.text}` : ''
        }`,
      });
    }
  }

  const previousNarrations: string[] = [];
  for (const s of slide.revealSteps) {
    if (s.id === step.id) break;
    const t = s.audio?.text?.trim();
    if (t) previousNarrations.push(t);
  }

  return {
    slideLabel: slide.label || 'Untitled slide',
    stepLabel: step.label || 'Step',
    elements,
    previousNarrations,
  };
}
